import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api-client.js';

interface Platform {
  id: string;
  platform: string;
  name: string;
  is_active: number;
  auto_post: number;
}

interface PlatformConfig {
  key: string;
  label: string;
  desc: string;
  color: string;
  iconBg: string;
  group: 'oauth' | 'credentials' | 'coming_soon';
  fields?: { key: string; label: string; type: string; placeholder: string }[];
}

const PLATFORMS: PlatformConfig[] = [
  // OAuth — frictionless
  {
    key: 'facebook_page', label: 'Facebook Page + Marketplace',
    desc: 'Post to your Page and list on Marketplace',
    color: '#3b82f6', iconBg: 'bg-blue-500/20',
    group: 'oauth',
  },
  // Credentials
  {
    key: 'craigslist', label: 'Craigslist',
    desc: 'Post to Craigslist auto section',
    color: '#a855f7', iconBg: 'bg-purple-500/20',
    group: 'credentials',
    fields: [
      { key: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com' },
      { key: 'password', label: 'Password', type: 'password', placeholder: 'Craigslist password' },
    ],
  },
  {
    key: 'autotrader', label: 'AutoTrader',
    desc: 'Sync inventory with AutoTrader',
    color: '#ef4444', iconBg: 'bg-red-500/20',
    group: 'credentials',
    fields: [
      { key: 'dealer_id', label: 'Dealer ID', type: 'text', placeholder: 'Your dealer ID' },
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'AutoTrader API key' },
    ],
  },
  // Coming soon
  {
    key: 'instagram', label: 'Instagram',
    desc: 'Share carousel posts to your Instagram',
    color: '#6b7280', iconBg: 'bg-surface-700',
    group: 'coming_soon',
  },
  {
    key: 'tiktok', label: 'TikTok',
    desc: 'Auto-generate short videos from your photos',
    color: '#6b7280', iconBg: 'bg-surface-700',
    group: 'coming_soon',
  },
  {
    key: 'offerup', label: 'OfferUp',
    desc: 'List on OfferUp marketplace',
    color: '#6b7280', iconBg: 'bg-surface-700',
    group: 'coming_soon',
  },
  {
    key: 'cargurus', label: 'CarGurus',
    desc: 'Sync with CarGurus listings',
    color: '#6b7280', iconBg: 'bg-surface-700',
    group: 'coming_soon',
  },
];

// Platform icon SVGs
function PlatformIcon({ platform, color }: { platform: string; color: string }) {
  if (platform.startsWith('facebook')) {
    return (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill={color}>
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    );
  }
  if (platform === 'instagram') {
    return (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill={color}>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    );
  }
  // Generic icon for other platforms
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={color}>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
    </svg>
  );
}

export default function ConnectAccountsPage() {
  const queryClient = useQueryClient();
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [credentialForms, setCredentialForms] = useState<Record<string, Record<string, string>>>({});
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [_justConnected, setJustConnected] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch connected platforms
  const { data: platformsRes, refetch, isLoading } = useQuery({
    queryKey: ['marketplace-platforms'],
    queryFn: () => api.get<Platform[]>('/marketplace/platforms'),
  });
  const connectedPlatforms = platformsRes?.data ?? [];

  const isConnected = useCallback((key: string) => {
    // For the merged FB card, check both facebook_page and facebook_marketplace
    if (key === 'facebook_page') {
      return connectedPlatforms.some((p) => (p.platform === 'facebook_page' || p.platform === 'facebook_marketplace') && p.is_active);
    }
    return connectedPlatforms.some((p) => p.platform === key && p.is_active);
  }, [connectedPlatforms]);

  // Detect mobile for OAuth flow (mobile can't do popup reliably)
  const isMobile = typeof navigator !== 'undefined' && (navigator.maxTouchPoints > 0 || window.innerWidth < 768);

  // Facebook OAuth — popup on desktop, redirect on mobile
  const fbAuthMutation = useMutation({
    mutationFn: () => api.get<any>(`/marketplace/facebook/auth-url?redirect_uri=${encodeURIComponent(window.location.origin + '/oauth/facebook/callback')}`),
    onSuccess: (data) => {
      if (data.data?.auth_url) {
        if (isMobile) {
          // Mobile: full-page redirect (popups don't work on mobile)
          sessionStorage.setItem('acp-oauth-return', '/connect');
          window.location.href = data.data.auth_url;
          return;
        }
        // Desktop: popup
        const popup = window.open(data.data.auth_url, 'fb_auth', 'width=600,height=700,scrollbars=yes');
        setConnectingPlatform('facebook');
        const interval = setInterval(() => {
          if (popup?.closed) {
            clearInterval(interval);
            setConnectingPlatform(null);
            refetch();
          }
        }, 500);
      }
    },
  });

  // Credential-based connection
  const connectMutation = useMutation({
    mutationFn: ({ platform, credentials }: { platform: string; credentials: Record<string, string> }) =>
      api.post('/marketplace/platforms/connect', { platform, credentials }),
    onSuccess: (_data, variables) => {
      setJustConnected((prev) => new Set(prev).add(variables.platform));
      setExpandedCard(null);
      setErrors((prev) => ({ ...prev, [variables.platform]: '' }));
      queryClient.invalidateQueries({ queryKey: ['marketplace-platforms'] });
    },
    onError: (_err, variables) => {
      setErrors((prev) => ({ ...prev, [variables.platform]: 'Connection failed. Check your credentials.' }));
    },
  });

  // Disconnect
  const disconnectMutation = useMutation({
    mutationFn: (platform: string) => api.delete(`/marketplace/platforms/${platform}`),
    onSuccess: (_data, platform) => {
      setJustConnected((prev) => {
        const next = new Set(prev);
        next.delete(platform);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['marketplace-platforms'] });
    },
  });

  // Listen for OAuth callback message from popup
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'oauth-success') {
        setConnectingPlatform(null);
        refetch();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [refetch]);

  const handleCredentialSubmit = (platform: PlatformConfig) => {
    const creds = credentialForms[platform.key] ?? {};
    const missing = platform.fields?.find((f) => !creds[f.key]?.trim());
    if (missing) {
      setErrors((prev) => ({ ...prev, [platform.key]: `${missing.label} is required` }));
      return;
    }
    setErrors((prev) => ({ ...prev, [platform.key]: '' }));
    connectMutation.mutate({ platform: platform.key, credentials: creds });
  };

  const updateField = (platform: string, field: string, value: string) => {
    setCredentialForms((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
  };

  const oauthPlatforms = PLATFORMS.filter((p) => p.group === 'oauth');
  const credentialPlatforms = PLATFORMS.filter((p) => p.group === 'credentials');
  const comingSoonPlatforms = PLATFORMS.filter((p) => p.group === 'coming_soon');
  const connectedCount = PLATFORMS.filter((p) => isConnected(p.key)).length;

  return (
    <div className="page max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <Link to="/dashboard" className="text-surface-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold">Connect Your Accounts</h1>
        </div>
        <p className="text-surface-500 text-sm ml-8">Link your platforms to post everywhere at once</p>
        {connectedCount > 0 && (
          <div className="ml-8 mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-900/30 border border-green-700/30">
            <div className="w-2 h-2 rounded-full bg-green-400" />
            <span className="text-green-400 text-xs font-medium">{connectedCount} connected</span>
          </div>
        )}
      </div>

      {/* One-Click (OAuth) Section */}
      <div className="mb-8">
        <p className="text-xs text-surface-500 uppercase tracking-wider font-semibold mb-3 ml-1">One-Click Connect</p>
        <div className="space-y-2">
          {oauthPlatforms.map((platform) => {
            const connected = isConnected(platform.key);
            const connecting = connectingPlatform === 'facebook' && platform.key.startsWith('facebook');

            return (
              <div key={platform.key} className={`card p-4 transition-all ${connected ? 'border-green-700/40' : ''}`}>
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${platform.iconBg}`}>
                    <PlatformIcon platform={platform.key} color={platform.color} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{platform.label}</p>
                    <p className="text-xs text-surface-500 truncate">{platform.desc}</p>
                  </div>

                  {/* Status / Action */}
                  {connected ? (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-green-400 text-xs font-semibold">Connected</span>
                      </div>
                      <button
                        onClick={() => disconnectMutation.mutate(platform.key)}
                        className="text-surface-600 hover:text-red-400 transition-colors text-xs"
                      >
                        Disconnect
                      </button>
                    </div>
                  ) : connecting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      <span className="text-blue-400 text-xs">Authorizing...</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => fbAuthMutation.mutate()}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-[1.02]"
                      style={{
                        background: `${platform.color}20`,
                        color: platform.color,
                        border: `1px solid ${platform.color}40`,
                      }}
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Credentials Section */}
      <div className="mb-8">
        <p className="text-xs text-surface-500 uppercase tracking-wider font-semibold mb-3 ml-1">Sign In Required</p>
        <div className="space-y-2">
          {credentialPlatforms.map((platform) => {
            const connected = isConnected(platform.key);
            const expanded = expandedCard === platform.key;
            const creds = credentialForms[platform.key] ?? {};
            const isSubmitting = connectMutation.isPending && connectMutation.variables?.platform === platform.key;
            const error = errors[platform.key];

            if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-surface-700 rounded w-48" />
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-surface-700 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
              <div key={platform.key} className={`card overflow-hidden transition-all ${connected ? 'border-green-700/40' : ''}`}>
                {/* Main row */}
                <div className="p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${platform.iconBg}`}>
                    <PlatformIcon platform={platform.key} color={platform.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{platform.label}</p>
                    <p className="text-xs text-surface-500 truncate">{platform.desc}</p>
                  </div>

                  {connected ? (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-green-400 text-xs font-semibold">Connected</span>
                      </div>
                      <button
                        onClick={() => disconnectMutation.mutate(platform.key)}
                        className="text-surface-600 hover:text-red-400 transition-colors text-xs"
                      >
                        Disconnect
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setExpandedCard(expanded ? null : platform.key)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                      style={{
                        background: `${platform.color}20`,
                        color: platform.color,
                        border: `1px solid ${platform.color}40`,
                      }}
                    >
                      {expanded ? 'Cancel' : 'Sign In'}
                    </button>
                  )}
                </div>

                {/* Expandable credentials form */}
                {expanded && !connected && (
                  <div className="px-4 pb-4 pt-0 border-t border-surface-700/50">
                    <div className="pt-3 space-y-3">
                      {platform.fields?.map((field) => (
                        <input
                          key={field.key}
                          type={field.type}
                          placeholder={field.placeholder}
                          value={creds[field.key] ?? ''}
                          onChange={(e) => updateField(platform.key, field.key, e.target.value)}
                          className="input w-full text-sm"
                          onKeyDown={(e) => e.key === 'Enter' && handleCredentialSubmit(platform)}
                        />
                      ))}
                      {error && <p className="text-red-400 text-xs">{error}</p>}
                      <button
                        onClick={() => handleCredentialSubmit(platform)}
                        disabled={isSubmitting}
                        className="w-full py-2.5 rounded-lg font-semibold text-sm text-white transition-colors disabled:opacity-50"
                        style={{ background: platform.color }}
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Connecting...
                          </span>
                        ) : 'Link Account'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Coming Soon Section */}
      <div className="mb-8">
        <p className="text-xs text-surface-500 uppercase tracking-wider font-semibold mb-3 ml-1">Coming Soon</p>
        <div className="space-y-2">
          {comingSoonPlatforms.map((platform) => (
            <div key={platform.key} className="card p-4 opacity-50">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${platform.iconBg}`}>
                  <PlatformIcon platform={platform.key} color={platform.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{platform.label}</p>
                  <p className="text-xs text-surface-500 truncate">{platform.desc}</p>
                </div>
                <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-800 text-surface-500">
                  Coming Soon
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom tip */}
      <div className="text-center pb-8">
        <p className="text-surface-600 text-xs">
          Connected accounts are used when you post a vehicle.
          <Link to="/marketplace?tab=platforms" className="text-brand-400 hover:text-brand-300 ml-1">
            Manage platforms
          </Link>
        </p>
      </div>
    </div>
  );
}
